// src/utils/eventTagMapping.js
// Maps event and user data from EventDashboard.jsx to dynamic scene generation event tags

/**
 * Maps event and user data from your system to normalized event tags
 * @param {object} event - Event data from events table
 * @param {object} user - User data with answers from event_orders
 * @param {object} options - Additional options for tag generation
 * @returns {object} - Normalized event tags for dynamic scene generation
 */
export const mapEventDashboardToEventTags = (event, user, options = {}) => {
  console.log('🏷️ Mapping EventDashboard data to event tags:', { event: event?.name, user: user?.email });
  
  // Extract athlete information from user answers
  const answers = user?.answers || {};
  
  // Extract sport from event type or name
  const sport = extractSportFromEvent(event);
  
  // Determine competitive level from event context
  const level = determineCompetitiveLevel(event, answers);
  
  // Extract team information
  const teamInfo = extractTeamInfo(answers);
  
  // Generate event tags in the format expected by dynamic scene generator
  const eventTags = {
    // Core sport and team information
    sport: sport,
    player_name: `${answers.athlete_first_name || ''} ${answers.athlete_last_name || ''}`.trim(),
    team_name: answers.team_name || teamInfo.name,
    level: level,
    
    // Event context
    event_type: mapEventType(event?.event_type),
    event_name: event?.name,
    season: extractSeason(event),
    venue: determineVenue(event),
    location: `${event?.city || ''}, ${event?.state || ''}`.trim(),
    
    // Player details
    athlete_first_name: answers.athlete_first_name,
    athlete_last_name: answers.athlete_last_name,
    position: answers.position || answers.player_position,
    player_number: answers.player_number || answers.jersey_number || answers.number,
    
    // Parent/contact information
    parent_name: `${user?.first_name || ''} ${user?.last_name || ''}`.trim(),
    parent_email: user?.email,
    parent_phone: user?.phone,
    
    // Team and organizational details
    team_colors: extractTeamColors(answers, teamInfo),
    coach_name: answers.coach_name || answers.coach,
    league: answers.league || answers.division,
    
    // Event timing and context
    start_date: event?.start_date,
    end_date: event?.end_date,
    time_of_day: determineTimeOfDay(event),
    
    // Photo and design preferences
    photo_type: options.photoType || 'individual', // individual, team, action
    design_style: options.designStyle || 'modern',
    mood: determineMood(event, level),
    background_preference: options.backgroundPreference,
    
    // Additional context
    achievements: extractAchievements(answers),
    special_notes: answers.special_notes || answers.notes,
    
    // Technical metadata
    user_id: user?.id,
    event_id: event?.id,
    order_status: user?.order_status,
    registration_date: user?.order_created_at || user?.created_at
  };
  
  // Remove empty/undefined values
  const cleanedEventTags = Object.fromEntries(
    Object.entries(eventTags).filter(([_, value]) => 
      value !== undefined && value !== null && value !== ''
    )
  );
  
  console.log('✅ Generated event tags:', cleanedEventTags);
  
  return cleanedEventTags;
};

/**
 * Extracts sport from event data
 */
const extractSportFromEvent = (event) => {
  if (!event) return 'baseball'; // Default
  
  // Check event_type field first
  if (event.event_type) {
    const eventType = event.event_type.toLowerCase();
    if (eventType.includes('baseball')) return 'baseball';
    if (eventType.includes('softball')) return 'softball';
    if (eventType.includes('basketball')) return 'basketball';
    if (eventType.includes('football')) return 'football';
    if (eventType.includes('soccer')) return 'soccer';
    if (eventType.includes('volleyball')) return 'volleyball';
    if (eventType.includes('tennis')) return 'tennis';
    if (eventType.includes('track')) return 'track';
    if (eventType.includes('wrestling')) return 'wrestling';
  }
  
  // Check event name if event_type doesn't match
  if (event.name) {
    const eventName = event.name.toLowerCase();
    if (eventName.includes('baseball')) return 'baseball';
    if (eventName.includes('softball')) return 'softball';
    if (eventName.includes('basketball')) return 'basketball';
    if (eventName.includes('football')) return 'football';
    if (eventName.includes('soccer')) return 'soccer';
    if (eventName.includes('volleyball')) return 'volleyball';
  }
  
  return 'baseball'; // Default fallback
};

/**
 * Determines competitive level from event and answers
 */
const determineCompetitiveLevel = (event, answers) => {
  // Check answers first for explicit level
  if (answers.level) return answers.level.toLowerCase();
  if (answers.division) {
    const division = answers.division.toLowerCase();
    if (division.includes('professional') || division.includes('pro')) return 'professional';
    if (division.includes('college') || division.includes('university')) return 'college';
    if (division.includes('high school') || division.includes('varsity')) return 'high-school';
    if (division.includes('middle school') || division.includes('junior')) return 'middle-school';
    if (division.includes('youth') || division.includes('little league')) return 'youth';
  }
  
  // Infer from event name
  if (event?.name) {
    const eventName = event.name.toLowerCase();
    if (eventName.includes('professional') || eventName.includes('pro')) return 'professional';
    if (eventName.includes('college') || eventName.includes('university')) return 'college';
    if (eventName.includes('high school') || eventName.includes('varsity')) return 'high-school';
    if (eventName.includes('middle school') || eventName.includes('junior')) return 'middle-school';
    if (eventName.includes('youth') || eventName.includes('little league')) return 'youth';
  }
  
  // Infer from age if available
  if (answers.age || answers.athlete_age) {
    const age = parseInt(answers.age || answers.athlete_age);
    if (age >= 18) return 'college';
    if (age >= 14) return 'high-school';
    if (age >= 11) return 'middle-school';
    return 'youth';
  }
  
  return 'youth'; // Default to youth sports
};

/**
 * Extracts team information from answers
 */
const extractTeamInfo = (answers) => {
  return {
    name: answers.team_name || answers.team,
    mascot: answers.team_mascot || answers.mascot,
    colors: answers.team_colors || answers.colors,
    coach: answers.coach_name || answers.coach
  };
};

/**
 * Maps your event types to standardized types
 */
const mapEventType = (eventType) => {
  if (!eventType) return 'regular_season';
  
  const type = eventType.toLowerCase();
  
  if (type.includes('championship') || type.includes('finals')) return 'championship';
  if (type.includes('tournament') || type.includes('playoff')) return 'tournament';
  if (type.includes('all star') || type.includes('allstar')) return 'all_star';
  if (type.includes('practice') || type.includes('training')) return 'practice';
  if (type.includes('team photo') || type.includes('picture')) return 'team_photo';
  if (type.includes('media day') || type.includes('photo day')) return 'media_day';
  if (type.includes('banquet') || type.includes('awards')) return 'awards_banquet';
  
  return 'regular_season';
};

/**
 * Extracts season from event date
 */
const extractSeason = (event) => {
  if (!event?.start_date) return new Date().getFullYear().toString();
  
  try {
    const eventDate = new Date(event.start_date);
    return eventDate.getFullYear().toString();
  } catch (error) {
    return new Date().getFullYear().toString();
  }
};

/**
 * Determines venue type from event data
 */
const determineVenue = (event) => {
  if (!event) return 'home';
  
  // Check if venue information is available
  if (event.venue) {
    const venue = event.venue.toLowerCase();
    if (venue.includes('home')) return 'home';
    if (venue.includes('away')) return 'away';
    if (venue.includes('neutral')) return 'neutral';
  }
  
  // Check address for venue type hints
  if (event.address1) {
    const address = event.address1.toLowerCase();
    if (address.includes('stadium') || address.includes('arena')) return 'stadium';
    if (address.includes('field') || address.includes('court')) return 'field';
    if (address.includes('park')) return 'park';
  }
  
  return 'home'; // Default assumption
};

/**
 * Determines time of day from event
 */
const determineTimeOfDay = (event) => {
  // Could be enhanced with actual event time data if available
  if (event?.event_time || event?.start_time) {
    try {
      const time = new Date(`2024-01-01 ${event.event_time || event.start_time}`);
      const hour = time.getHours();
      
      if (hour >= 6 && hour < 12) return 'morning';
      if (hour >= 12 && hour < 17) return 'afternoon';
      if (hour >= 17 && hour < 20) return 'evening';
      return 'night';
    } catch (error) {
      // Ignore time parsing errors
    }
  }
  
  return 'afternoon'; // Default assumption for sports events
};

/**
 * Determines mood from event and level
 */
const determineMood = (event, level) => {
  const eventType = event?.event_type?.toLowerCase() || '';
  
  if (eventType.includes('championship') || eventType.includes('finals')) {
    return 'triumphant';
  }
  
  if (eventType.includes('tournament') || eventType.includes('playoff')) {
    return 'competitive';
  }
  
  if (level === 'professional') return 'elite';
  if (level === 'college') return 'spirited';
  if (level === 'youth') return 'fun';
  
  return 'energetic'; // Default sports mood
};

/**
 * Extracts team colors from answers
 */
const extractTeamColors = (answers, teamInfo) => {
  const colors = answers.team_colors || teamInfo.colors || answers.colors;
  
  if (Array.isArray(colors)) return colors;
  
  if (typeof colors === 'string') {
    // Split comma-separated colors
    return colors.split(',').map(color => color.trim());
  }
  
  return []; // No colors specified
};

/**
 * Extracts achievements from answers
 */
const extractAchievements = (answers) => {
  const achievements = [];
  
  // Look for various achievement fields
  if (answers.achievements) {
    if (Array.isArray(answers.achievements)) {
      achievements.push(...answers.achievements);
    } else {
      achievements.push(answers.achievements);
    }
  }
  
  if (answers.awards) {
    if (Array.isArray(answers.awards)) {
      achievements.push(...answers.awards);
    } else {
      achievements.push(answers.awards);
    }
  }
  
  // Look for specific achievement indicators
  if (answers.mvp || answers.is_mvp) achievements.push('MVP');
  if (answers.captain || answers.is_captain) achievements.push('Team Captain');
  if (answers.all_star || answers.is_all_star) achievements.push('All-Star');
  
  return achievements;
};

/**
 * Creates event tags for multiple users (batch processing)
 * @param {object} event - Event data
 * @param {Array} users - Array of user data
 * @param {object} options - Options for tag generation
 * @returns {Array} - Array of {user, eventTags} objects
 */
export const mapEventDashboardBatch = (event, users, options = {}) => {
  console.log(`🔄 Batch mapping event tags for ${users.length} users`);
  
  return users.map(user => ({
    user,
    eventTags: mapEventDashboardToEventTags(event, user, options)
  }));
};

/**
 * Creates a standardized photo context object for UI display
 * @param {object} eventTags - Generated event tags
 * @returns {object} - Photo context for UI components
 */
export const createPhotoContext = (eventTags) => {
  return {
    title: `${eventTags.player_name || 'Athlete'} - ${eventTags.team_name || 'Team'}`,
    subtitle: `${eventTags.level || 'Youth'} ${eventTags.sport || 'Sports'} - ${eventTags.season || '2024'}`,
    details: {
      sport: eventTags.sport,
      level: eventTags.level,
      position: eventTags.position,
      number: eventTags.player_number,
      team: eventTags.team_name,
      event: eventTags.event_name
    },
    context: {
      isTournament: eventTags.event_type === 'tournament' || eventTags.event_type === 'championship',
      isTeamPhoto: eventTags.photo_type === 'team',
      hasAchievements: eventTags.achievements && eventTags.achievements.length > 0,
      mood: eventTags.mood
    }
  };
};

export default {
  mapEventDashboardToEventTags,
  mapEventDashboardBatch,
  createPhotoContext
}; 